import { create } from 'zustand';
import { supabase } from '../lib/supabase';

export type DisplayMode = 'grid' | 'masonry' | 'carousel';

interface GalleryImage {
  id: string;
  url: string;
  title?: string;
  description?: string;
  altText?: string;
  width?: number;
  height?: number;
  position: number;
}

interface GallerySettings {
  displayMode: DisplayMode;
  gridColumns: number;
  spacing: number;
}

interface LoadingState {
  settings: boolean;
  upload: boolean;
  delete: boolean;
  reorder: boolean;
  initialize: boolean;
}

interface GallerySystemStore {
  settings: GallerySettings;
  images: GalleryImage[];
  loading: LoadingState;
  error: string | null;
  initialized: boolean;
  updateSettings: (chatbotPublicId: string, settings: Partial<GallerySettings>) => Promise<void>;
  uploadImage: (chatbotPublicId: string, file: File, metadata?: Partial<Omit<GalleryImage, 'id' | 'url' | 'position'>>) => Promise<void>;
  deleteImage: (chatbotPublicId: string, imageId: string) => Promise<void>;
  reorderImages: (chatbotPublicId: string, imageIds: string[]) => Promise<void>;
  initialize: (chatbotPublicId: string) => Promise<void>;
}

const DEFAULT_SETTINGS: GallerySettings = {
  displayMode: 'grid',
  gridColumns: 3,
  spacing: 4
};

const DEFAULT_LOADING_STATE: LoadingState = {
  settings: false,
  upload: false,
  delete: false,
  reorder: false,
  initialize: false
};

export const useGallerySystem = create<GallerySystemStore>((set, get) => ({
  settings: DEFAULT_SETTINGS,
  images: [],
  loading: DEFAULT_LOADING_STATE,
  error: null,
  initialized: false,

  updateSettings: async (chatbotPublicId, settings) => {
    try {
      set({ loading: { ...get().loading, settings: true }, error: null });

      const { data, error: checkError } = await supabase
        .from('gallery_settings')
        .select('id')
        .eq('chatbot_public_id', chatbotPublicId)
        .single();

      if (checkError && checkError.code !== 'PGRST116') throw checkError;

      // Convert from camelCase to snake_case for database
      const dbSettings = {
        ...(settings.displayMode !== undefined && { display_mode: settings.displayMode }),
        ...(settings.gridColumns !== undefined && { grid_columns: settings.gridColumns }),
        ...(settings.spacing !== undefined && { spacing: settings.spacing })
      };

      if (data) {
        // Update existing settings
        const { error } = await supabase
          .from('gallery_settings')
          .update(dbSettings)
          .eq('id', data.id);

        if (error) throw error;
      } else {
        // Insert new settings
        const { error } = await supabase
          .from('gallery_settings')
          .insert({
            chatbot_public_id: chatbotPublicId,
            ...dbSettings
          });

        if (error) throw error;
      }

      // Update local state
      set(state => ({
        settings: {
          ...state.settings,
          ...settings
        }
      }));
    } catch (err) {
      console.error('Error updating settings:', err);
      set({ error: err instanceof Error ? err.message : 'Error updating settings' });
    } finally {
      set(state => ({ loading: { ...state.loading, settings: false } }));
    }
  },

  uploadImage: async (chatbotPublicId, file, metadata = {}) => {
    try {
      set({ loading: { ...get().loading, upload: true }, error: null });

      // Upload image to storage
      const fileExt = file.name.split('.').pop();
      const filePath = `gallery/${chatbotPublicId}/${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from('chatbot-assets')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('chatbot-assets')
        .getPublicUrl(filePath);

      // Get current max position
      const maxPosition = Math.max(0, ...get().images.map(img => img.position));

      // Insert image record
      const { error: dbError } = await supabase
        .from('gallery_images')
        .insert({
          chatbot_public_id: chatbotPublicId,
          url: publicUrl,
          position: maxPosition + 1,
          ...metadata
        });

      if (dbError) throw dbError;

      // Refresh images
      await get().initialize(chatbotPublicId);
    } catch (err) {
      console.error('Error uploading image:', err);
      set({ error: err instanceof Error ? err.message : 'Error uploading image' });
    } finally {
      set(state => ({ loading: { ...state.loading, upload: false } }));
    }
  },

  deleteImage: async (chatbotPublicId, imageId) => {
    try {
      set({ loading: { ...get().loading, delete: true }, error: null });

      const { error } = await supabase
        .from('gallery_images')
        .delete()
        .eq('id', imageId)
        .eq('chatbot_public_id', chatbotPublicId);

      if (error) throw error;

      set(state => ({
        images: state.images.filter(img => img.id !== imageId)
      }));
    } catch (err) {
      console.error('Error deleting image:', err);
      set({ error: err instanceof Error ? err.message : 'Error deleting image' });
    } finally {
      set(state => ({ loading: { ...state.loading, delete: false } }));
    }
  },

  reorderImages: async (chatbotPublicId, imageIds) => {
    try {
      set({ loading: { ...get().loading, reorder: true }, error: null });

      // Update positions
      for (let i = 0; i < imageIds.length; i++) {
        const { error } = await supabase
          .from('gallery_images')
          .update({ position: i })
          .eq('id', imageIds[i])
          .eq('chatbot_public_id', chatbotPublicId);

        if (error) throw error;
      }

      // Refresh images
      await get().initialize(chatbotPublicId);
    } catch (err) {
      console.error('Error reordering images:', err);
      set({ error: err instanceof Error ? err.message : 'Error reordering images' });
    } finally {
      set(state => ({ loading: { ...state.loading, reorder: false } }));
    }
  },

  initialize: async (chatbotPublicId) => {
    try {
      set({ loading: { ...get().loading, initialize: true }, error: null });

      // Fetch settings
      const { data: settingsData, error: settingsError } = await supabase
        .from('gallery_settings')
        .select('*')
        .eq('chatbot_public_id', chatbotPublicId)
        .single();

      if (settingsError && settingsError.code !== 'PGRST116') throw settingsError;

      // Fetch images
      const { data: imagesData, error: imagesError } = await supabase
        .from('gallery_images')
        .select('*')
        .eq('chatbot_public_id', chatbotPublicId)
        .order('position');

      if (imagesError) throw imagesError;

      // Convert database types to our expected types
      const mappedSettings: GallerySettings = settingsData
        ? {
            displayMode: (settingsData.display_mode || 'grid') as DisplayMode,
            gridColumns: settingsData.grid_columns || 3,
            spacing: settingsData.spacing || 4
          }
        : DEFAULT_SETTINGS;

      // Map images data to our expected format
      const mappedImages: GalleryImage[] = imagesData
        ? imagesData.map(img => ({
            id: img.id,
            url: img.url,
            title: img.title || undefined,
            description: img.description || undefined,
            altText: img.alt_text || undefined,
            width: img.width || undefined,
            height: img.height || undefined,
            position: img.position || 0
          }))
        : [];

      set({
        settings: mappedSettings,
        images: mappedImages,
        initialized: true
      });
    } catch (err) {
      console.error('Error initializing gallery:', err);
      set({ 
        error: err instanceof Error ? err.message : 'Error initializing gallery',
        settings: DEFAULT_SETTINGS,
        images: [],
        initialized: true
      });
    } finally {
      set(state => ({ loading: { ...state.loading, initialize: false } }));
    }
  }
}));